import React from 'react';
import { Box, Text, Flex, Icon, useColorModeValue, Spinner, Badge, Button, Image } from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdCalendarToday, MdFitnessCenter, MdBusiness, MdWarning } from 'react-icons/md';
import { getClassTypeIcon } from 'utils/classIcons';
import { getClassDisplayText } from 'utils/instructorClassWorkflows';

export default function DateSidebar({ selectedDate, classes = [], loading, onClassDetails, customTitle, disabled = false, viewMode = 'day', hasMultipleFacilities = false }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const facilityTagBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const iconFilter = 'brightness(0) invert(1)'; // Always white for blue backgrounds

  // Format the date as "Thursday, January 15th"
  // Note: selectedDate is a calendar date selection, not a timestamp, so don't use timezone conversion
  const formatDate = (date) => {
    if (!date) return '';

    const options = { weekday: 'long', month: 'long', day: 'numeric' };
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

    // Add ordinal suffix (st, nd, rd, th)
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);

    // Replace the day number with day + suffix
    return formattedDate.replace(/\d+/, `${day}${suffix}`);
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const options = { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Los_Angeles' };
    return date.toLocaleTimeString('en-US', options);
  };

  const formatClassDate = (timestamp) => {
    const date = new Date(timestamp);
    // Class timestamps should use LA timezone since they represent specific moments in time
    const options = { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' };
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
    // Get the day in LA timezone
    const dayOptions = { day: 'numeric', timeZone: 'America/Los_Angeles' };
    const day = parseInt(new Intl.DateTimeFormat('en-US', dayOptions).format(date), 10);
    const suffix = getOrdinalSuffix(day);
    return formattedDate.replace(/\d+/, `${day}${suffix}`);
  };

  const calculateLength = (startTime, endTime) => {
    const lengthInMs = endTime - startTime;
    const lengthInMinutes = Math.round(lengthInMs / 1000 / 60);
    return `${lengthInMinutes}min`;
  };

  return (
    <Card p='20px' align='center' direction='column' w='100%'>
      <Flex alignItems='center' w='100%' mb={classes.length > 0 ? '20px' : '0'}>
        <Icon as={MdCalendarToday} color={brandColor} w='28px' h='28px' me='12px' />
        <Text
          color={textColor}
          fontSize='xl'
          fontWeight='700'
          lineHeight='100%'>
          {customTitle || formatDate(selectedDate)}
        </Text>
      </Flex>

      {loading ? (
        <Flex justify='center' align='center' w='100%' py='40px'>
          <Spinner color={brandColor} size='lg' thickness='3px' />
        </Flex>
      ) : classes.length === 0 ? (
        <Flex
          direction='column'
          align='center'
          justify='center'
          w='100%'
          py='40px'
        >
          <Icon as={MdFitnessCenter} color={textColorSecondary} w='48px' h='48px' mb='12px' opacity={0.3} />
          <Text color={textColorSecondary} fontSize='sm' fontWeight='500' textAlign='center'>
            No classes scheduled
          </Text>
        </Flex>
      ) : (
        <Flex direction='column' w='100%' gap='10px'>
          {classes.map((classItem) => {
            const hasTag = hasMultipleFacilities && classItem.facilityName;
            const className = getClassDisplayText(classItem.ClassName, 'Unnamed Class');
            const facilityName = getClassDisplayText(
              classItem.facilityName || classItem.FacilityName || classItem.FirstName,
              'Facility'
            );
            return (
              <Flex
                key={classItem.id}
                data-testid={`calendar-class-card-${classItem.id}`}
                w='100%'
                p='12px'
                bg={cardBg}
                borderRadius='10px'
                border='1px solid'
                borderColor={borderColor}
                direction='column'
                gap='6px'
                transition='all 0.2s'
                cursor={disabled ? 'default' : 'pointer'}
                onClick={() => !disabled && onClassDetails?.(classItem)}
                _hover={{
                  borderColor: brandColor,
                  boxShadow: 'sm',
                }}
                position='relative'
              >
                {/* Facility Tag - Top Left */}
                {hasTag && (
                  <Flex
                    position='absolute'
                    top='12px'
                    left='12px'
                    align='center'
                    gap='4px'
                    bg={facilityTagBg}
                    px='8px'
                    py='4px'
                    borderRadius='6px'
                  >
                    <Icon as={MdBusiness} color={brandColor} w='14px' h='14px' />
                    <Text color={brandColor} fontSize='xs' fontWeight='600' whiteSpace='nowrap'>
                      {classItem.facilityName}
                    </Text>
                  </Flex>
                )}
                {/* Top Row - Icon, Class Name & Time, Duration Badge */}
                <Flex align='center' justify='space-between' mt={hasTag ? '32px' : '0'}>
                  <Flex align='center' flex='1' minW='0'>
                    <Flex
                      bg={brandColor}
                      borderRadius='8px'
                      w='32px'
                      h='32px'
                      align='center'
                      justify='center'
                      me='10px'
                      flexShrink='0'
                    >
                      {getClassTypeIcon(classItem.ClassName || classItem.ClassType) ? (
                        <Image
                          src={getClassTypeIcon(classItem.ClassName || classItem.ClassType)}
                          alt={className}
                          w='20px'
                          h='20px'
                          objectFit='contain'
                          filter={iconFilter}
                        />
                      ) : (
                        <Icon as={MdFitnessCenter} color='white' w='16px' h='16px' />
                      )}
                    </Flex>
                    <Box flex='1' minW='0'>
                      <Flex align='center' gap='6px'>
                        <Text color={textColor} fontSize='sm' fontWeight='700' lineHeight='1.2' noOfLines={1}>
                          {className}
                        </Text>
                        {classItem.isLeadPending && (
                          <Icon
                            as={MdWarning}
                            color='yellow.500'
                            w='16px'
                            h='16px'
                            title='Account information missing'
                          />
                        )}
                        {classItem.isPendingApproval && (
                          <Badge
                            colorScheme='orange'
                            fontSize='9px'
                            fontWeight='600'
                            px='4px'
                            py='1px'
                            borderRadius='4px'
                            textTransform='uppercase'
                          >
                            Pending Approval
                          </Badge>
                        )}
                        {classItem.HasPendingEdit && (
                          <Badge
                            colorScheme='orange'
                            fontSize='9px'
                            fontWeight='600'
                            px='4px'
                            py='1px'
                            borderRadius='3px'
                            textTransform='uppercase'
                          >
                            Pending Edit
                          </Badge>
                        )}
                      </Flex>
                      <Text color={textColorSecondary} fontSize='xs' fontWeight='500' mt='2px'>
                        {viewMode === 'week' ? formatClassDate(classItem.Date) : formatTime(classItem.Date)}
                      </Text>
                    </Box>
                  </Flex>
                  <Badge
                    colorScheme='brand'
                    fontSize='xs'
                    fontWeight='600'
                    px='6px'
                    py='2px'
                    borderRadius='4px'
                    ml='8px'
                    flexShrink='0'
                  >
                    {calculateLength(classItem.Date, classItem.EndDate)}
                  </Badge>
                </Flex>

                {/* Middle Row - Facility (centered to match class name alignment) */}
                <Flex align='center' justify='space-between'>
                  {/* Spacer matching icon width */}
                  <Box w='32px' me='10px' flexShrink='0' />

                  {/* Facility content (matches class name Box structure) */}
                  <Flex align='center' justify='center' flex='1' minW='0'>
                    <Flex
                      data-testid="calendar-sidebar-facility-icon"
                      w='20px'
                      h='20px'
                      borderRadius='7px'
                      bg={facilityTagBg}
                      align='center'
                      justify='center'
                      me='6px'
                      flexShrink='0'
                    >
                      <Icon as={MdBusiness} color={brandColor} w='12px' h='12px' />
                    </Flex>
                    <Text color={textColorSecondary} fontSize='xs' fontWeight='600' noOfLines={1}>
                      {facilityName}
                    </Text>
                  </Flex>

                  {/* Spacer matching badge area */}
                  <Box w='calc(var(--badge-width, 60px) + 8px)' flexShrink='0' />
                </Flex>

                {/* Bottom Row - Details Button (now just for visual indication) */}
                <Flex justify='flex-end' mt='-2px'>
                  <Button
                    size='xs'
                    variant='ghost'
                    colorScheme='brand'
                    onClick={(e) => {
                      e.stopPropagation();
                      !disabled && onClassDetails?.(classItem);
                    }}
                    flexShrink='0'
                    isDisabled={disabled}
                    pointerEvents='none'
                    opacity={0.7}
                  >
                    Details
                  </Button>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Card>
  );
}
